import { createRef, useContext, useEffect } from 'react';
import { Box, IconButton, ModalBody, ModalFooter, ModalHeader, Text, Textarea, theme } from '@chakra-ui/react';
import { CbMultiSelect, StylesConfig } from 'ui/components';
import { CbAvatar } from 'ui/components/CbAvatar';
import { CbButton } from 'ui/components/Buttons/CbButton';
import { NewsfeedContext } from 'providers';
import Gif from '../../Icons/Gif';
import Attachment from '../../Icons/Attachment';
import styles from './CbCreatePost.module.css';
import { AUTH_STORAGE_KEY } from 'ui/types';
// @ts-ignore
import Microlink from '@microlink/react';
import { ICON_BY_NAME } from 'ui/components/Icons/Icon';
import { FileChip } from 'ui/components/FileChip';
import Close from '../../Icons/Close';
export const CbCreatePost = ({
  isPosting,
  onGIF,
  onPost,
  onRemoveImage,
  imageURL,
  onFiles,
  setTextContent,
  textContent,
  setPreviewUrl,
  previewUrl,
  fileData,
  removeFile
}) => {
  const postInputRef = createRef<HTMLTextAreaElement>();
  const {
    additionalFeeds,
    currentTeam,
    postableTeams,
    setAdditionalFeeds
  } = useContext(NewsfeedContext);
  useEffect(() => {
    postInputRef.current?.focus();
  }, []);
  const suppressLinkPreview = () => {
    setPreviewUrl(null);
  };
  const renderLinkPreview = () => {
    return previewUrl && <Box className={styles.microlinkOuterContainer}>
          <Box className={styles.microlinkInnerContainer}>
            <IconButton aria-label="Remove link preview" size="xs" borderRadius="0" position="absolute" zIndex={theme.zIndices.docked} onClick={() => suppressLinkPreview()} icon={<ICON_BY_NAME.close color="black" fontSize="8px" />} />
            {/* key prop is used so that the component rerenders when previewUrl changes */}
            <Microlink apiKey={process.env.MICROLINK_API_KEY} key={previewUrl} url={previewUrl} />
          </Box>
        </Box>;
  };
  const handleContentChange = content => {
    setTextContent(content);

    //HTTP and HTTPS URLs that start with protocol can be validated using the following regular expression
    let previewUrl = content.match(/https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#()?&//=]*)/gim)?.slice(-1)[0];
    setPreviewUrl(previewUrl);
  };
  const {
    profileImage
  } = JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) || '{}');
  const postHeader = () => {
    if (currentTeam) return `Posting to ${currentTeam.name}`;
    if (additionalFeeds.length > 0) return 'Posting to selected teams';
    return 'Posting Globally';
  };
  const multiSelectStyles = {
    control: base => ({
      ...base,
      borderRadius: 12,
      minWidth: 325
    }),
    multiValue: base => ({
      ...base,
      borderRadius: 12
    }),
    placeholder: base => ({
      ...base,
      fontSize: 14
    })
  } as StylesConfig;
  return <>
      <ModalHeader className={styles.header} data-sentry-element="ModalHeader" data-sentry-source-file="CbCreatePost.tsx">
        <CbAvatar avatarSize="sm" image={profileImage} data-sentry-element="CbAvatar" data-sentry-source-file="CbCreatePost.tsx" />
        <Text className={styles.title} data-sentry-element="Text" data-sentry-source-file="CbCreatePost.tsx">{postHeader()}</Text>
      </ModalHeader>
      <ModalBody data-sentry-element="ModalBody" data-sentry-source-file="CbCreatePost.tsx">
        <Textarea data-testid="rich-newsfeed-modal" className={styles.postContent} onChange={event => handleContentChange(event.target.value)} placeholder="What do you want to say?" ref={postInputRef} resize="vertical" rows={4} value={textContent} data-sentry-element="Textarea" data-sentry-source-file="CbCreatePost.tsx" />
        {imageURL && <div className={styles.imagePreview}>
            <img src={imageURL} />
            <CbButton className={styles.dismiss} onClick={onRemoveImage} variant="secondary">
              <Close />
            </CbButton>
          </div>}
        {fileData && fileData.length > 0 && <div className={styles.fileChips}>
            {fileData.map(data => <FileChip key={data.signed_blob_id} file={data.file} removeFile={removeFile} />)}
          </div>}
        {renderLinkPreview()}
      </ModalBody>
      <hr />
      <ModalFooter className={styles.footer} data-sentry-element="ModalFooter" data-sentry-source-file="CbCreatePost.tsx">
        <div className={styles.photoOptions}>
          <div className={`${styles.photoOption} ${styles.fileOption}`} onClick={onFiles}>
            <Attachment boxSize="24px" data-sentry-element="Attachment" data-sentry-source-file="CbCreatePost.tsx" />
            <Text data-sentry-element="Text" data-sentry-source-file="CbCreatePost.tsx">Attachments</Text>
          </div>

          <div className={styles.photoOption} onClick={onGIF}>
            <Gif boxSize="24px" data-sentry-element="Gif" data-sentry-source-file="CbCreatePost.tsx" />
            <Text data-sentry-element="Text" data-sentry-source-file="CbCreatePost.tsx">GIF</Text>
          </div>

          <div className={styles.feedSelect}>
            <CbMultiSelect noOptionsMessage={() => 'No results found'} onChange={setAdditionalFeeds as (value: any[]) => void} options={postableTeams}
          /* 
              If currentTeam is present then it means we are posting from a team feed.
              If it is not then we must be posting from the global feed
            */ placeholder={!!currentTeam ? 'Also Post to' : 'Only Post to'} styles={multiSelectStyles} value={additionalFeeds} data-sentry-element="CbMultiSelect" data-sentry-source-file="CbCreatePost.tsx" />
          </div>
        </div>
        <CbButton aria-hidden={false} aria-label="Post" className={`${styles.post} ${styles.postWithSelect}`} data-testid={'post-newsfeed-button'} isDisabled={!textContent || isPosting} isLoading={isPosting} loadingText="Posting" onClick={() => onPost({
        text: textContent,
        signed_blob_ids: fileData.map(data => data.signed_blob_id),
        preview_url: previewUrl,
        image_url: imageURL,
        team_ids: additionalFeeds.map(feed => feed.value)
      })} data-sentry-element="CbButton" data-sentry-source-file="CbCreatePost.tsx">
          Post
        </CbButton>
      </ModalFooter>
    </>;
};